// useCustomerHistory — fetches every other conversation Kimi has had with
// this customer email, across every brand and channel. Powers:
//   - The "Msgs" stat on Customer panel
//   - The "Full history with this customer" expandable section
//
// Endpoint: GET /api/conversations/by-customer?email=...&exclude=:id
// Returns: { conversations: [...], messageCount: number }
//
// We hit the backend (not direct anon Supabase) so we can compute messageCount
// in one query rather than fetching every message client-side.

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';

export interface CustomerHistoryConversation {
  id: string;
  brand: 'md' | 'hk';
  channel: string;
  subject: string | null;
  ai_summary: string | null;
  status: string;
  last_message_at: string;
  last_message_direction: 'inbound' | 'outbound' | null;
}

export interface CustomerHistoryResponse {
  conversations: CustomerHistoryConversation[];
  messageCount: number;
}

export function useCustomerHistory(
  email: string | null | undefined,
  excludeConversationId: string | undefined,
) {
  return useQuery<CustomerHistoryResponse>({
    queryKey: ['customer-history', email, excludeConversationId],
    queryFn: () =>
      apiGet<CustomerHistoryResponse>(
        `/api/conversations/by-customer?email=${encodeURIComponent(email!)}` +
          (excludeConversationId ? `&exclude=${excludeConversationId}` : ''),
      ),
    enabled: !!email,
    staleTime: 60_000,
    retry: 0,
  });
}
